import React from "react";
//#region types
import { WintSuperkollAPIModel, WintUtilsWebApi } from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
import { Amount } from "models/utilTypes/currencyType";
import { CurrencyEnums } from "models/enums";
import { IconDefinitionConstants } from "utils/constants";
import { IconProfileInfo } from "models/propTypes/userProfilePropTypes";
import { ArticleFormTypes } from "models/formTypes";
import { ArticlesListProps } from "models/propTypes/articlePropTypes";
import { TableHandles } from "models/propTypes/simpleTableProps";
//#endregion
//#region components
import { useNavigate, useLocation } from "react-router-dom";
import { TableAsync, useFilterContext } from "components/common";
import { useInvoiceService, useArticleService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance, UrlHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";

const ArticlesList: React.ForwardRefRenderFunction<TableHandles, ArticlesListProps> = (props, ref) => {
  const invoiceService = useInvoiceService();
  const location = useLocation();
  const navigate = useNavigate();
  const articleService = useArticleService();

  const { reloadKey } = props;
  const { filter } = useFilterContext<ArticleFormTypes.Filter>();

  const filterFunc = React.useMemo(
    () => (baseFilter: WintUtilsWebApi.Filter) => articleService.reportFilter(baseFilter, filter, true),
    [filter]
  );

  const onRowClick = (article: WintSuperkollAPIModel.Article, rowIndex: number) => {
    navigate(UrlHelper.formatPath(`/Article/${article.id}`), {
      state: { background: location, preserveSearch: true },
    });
  };

  return (
    <TableAsync<WintSuperkollAPIModel.Article>
      tableRef={ref}
      properties={{
        text: {
          description: TranslationKeys.Common.NameType,
          helpText: TranslationKeys.Article.NameAndTypeHelpText,
          formatter: (article: WintSuperkollAPIModel.Article) =>
            new IconProfileInfo(
              article.text ?? "",
              IconDefinitionConstants.WBox,
              article.euVatProductType !== undefined
                ? invoiceService.getEuVatCategoryText(article.euVatProductType)
                : ""
            ),
          className: clsx(cssExports.textBlack, cssExports.iconProfileContainerFullHeight),
        },
        unitPrice: {
          description: TranslationKeys.Common.StandardPrice,
          helpText: TranslationKeys.Article.StandardPriceHelpText,
          formatter: (article: WintSuperkollAPIModel.Article) =>
            new Amount(CurrencyEnums.CurrencyCode.SEK, article.unitPrice),
          alignment: "right",
        },
        totalQuantityForLastYearlyPeriod: {
          description: TranslationKeys.Article.TotalQuantityForLastYearlyPeriod,
          helpText: TranslationKeys.Article.TotalQuantityForLastYearlyPeriodHelpText,
          formatter: (article: WintSuperkollAPIModel.Article) =>
            `${article.totalQuantityForLastYearlyPeriod} ${article.unitText ? article.unitText : ""}`,
          alignment: "right",
          classification: "amount",
        },
        totalAmountForLastYearlyPeriod: {
          description: TranslationKeys.Article.TotalAmountForLastYearlyPeriod,
          helpText: TranslationKeys.Article.TotalAmountForLastYearlyPeriodHelpText,
          formatter: (article: WintSuperkollAPIModel.Article) =>
            new Amount(CurrencyEnums.CurrencyCode.SEK, article.totalAmountForLastYearlyPeriod),
          alignment: "right",
          className: cssExports.textBlack,
          classification: "amount",
        },
      }}
      filter={filterFunc}
      rowClickHandler={onRowClick}
      pagination="advanced"
      reloadKey={reloadKey}
      maxRowCount={10}
      exportFileName={
        props.exportFileName ?? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Articles)
      }
    />
  );
};

export default React.forwardRef(ArticlesList);
